import type { ThemeManifest } from "@crashx/theme-schema";
import { themeAssetUrl } from "../theme/loadTheme.js";
import { SYNTH_PRESETS, type AudioCue } from "./synthPresets.js";

interface Voice {
  cue: AudioCue;
  source: AudioScheduledSourceNode;
  gain: GainNode;
}
export class AudioEngine {
  private context: AudioContext | null = null;
  private theme: ThemeManifest | null = null;
  private muted = true;
  private voices = new Set<Voice>();
  private loading = new Map<AudioCue, AbortController>();
  private generation = 0;
  constructor(private createContext: () => AudioContext = () => new AudioContext()) {}
  configure(theme: ThemeManifest): void {
    this.stopAll();
    this.theme = theme;
  }
  async unlock(): Promise<boolean> {
    try {
      this.context ??= this.createContext();
      await this.context.resume();
      return this.context.state === "running";
    } catch {
      return false;
    }
  }
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) this.stopAll();
  }
  play(cue: AudioCue): void {
    if (
      this.muted ||
      !this.theme ||
      this.context?.state !== "running" ||
      this.voices.size >= 12 ||
      this.loading.has(cue)
    )
      return;
    if (cue === "engine" && [...this.voices].some((voice) => voice.cue === cue)) return;
    const path = this.theme.audio[cue];
    if (path) {
      void this.playFile(cue, path);
      return;
    }
    if (this.theme.audio.synthFallback) this.synthesize(cue);
  }
  stop(cue: AudioCue): void {
    this.loading.get(cue)?.abort();
    this.loading.delete(cue);
    for (const voice of this.voices) if (voice.cue === cue) this.dispose(voice);
  }
  stopAll(): void {
    this.generation++;
    for (const controller of this.loading.values()) controller.abort();
    this.loading.clear();
    for (const voice of this.voices) this.dispose(voice);
  }
  async destroy(): Promise<void> {
    this.stopAll();
    await this.context?.close();
    this.context = null;
  }
  private dispose(voice: Voice): void {
    if (!this.voices.delete(voice)) return;
    voice.source.onended = null;
    try {
      voice.source.stop();
    } catch {
      /* A finished one-shot is already stopped. */
    }
    voice.source.disconnect();
    voice.gain.disconnect();
  }
  private connect(cue: AudioCue, source: AudioScheduledSourceNode, gain: GainNode): void {
    const context = this.context;
    if (!context) return;
    source.connect(gain);
    gain.connect(context.destination);
    const voice = { cue, source, gain };
    this.voices.add(voice);
    source.onended = () => this.dispose(voice);
    source.start();
  }
  private synthesize(cue: AudioCue): void {
    const context = this.context;
    if (!context) return;
    const preset = SYNTH_PRESETS[cue];
    const source = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    source.type = preset.wave;
    source.frequency.setValueAtTime(preset.from, now);
    source.frequency.exponentialRampToValueAtTime(preset.to, now + preset.seconds);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(preset.volume, now + 0.015);
    this.connect(cue, source, gain);
    if (!preset.loop) {
      gain.gain.exponentialRampToValueAtTime(0.0001, now + preset.seconds);
      source.stop(now + preset.seconds + 0.02);
    }
  }
  private async playFile(cue: AudioCue, path: string): Promise<void> {
    const context = this.context;
    const theme = this.theme;
    if (!context || !theme) return;
    const generation = this.generation;
    const controller = new AbortController();
    this.loading.set(cue, controller);
    try {
      const response = await fetch(themeAssetUrl(theme.slug, path, theme.version), {
        cache: "no-store",
        signal: controller.signal,
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("Audio unavailable");
      const buffer = await context.decodeAudioData(await response.arrayBuffer());
      if (controller.signal.aborted || generation !== this.generation || this.muted) return;
      const source = context.createBufferSource();
      const gain = context.createGain();
      source.buffer = buffer;
      source.loop = cue === "engine";
      gain.gain.setValueAtTime(0.15, context.currentTime);
      this.connect(cue, source, gain);
    } catch {
      if (
        !controller.signal.aborted &&
        generation === this.generation &&
        !this.muted &&
        theme.audio.synthFallback
      )
        this.synthesize(cue);
    } finally {
      if (this.loading.get(cue) === controller) this.loading.delete(cue);
    }
  }
}
export const audioEngine = new AudioEngine();
